---
title: "How the AI interview scheduler works"
description: "The AI scheduler in Chosen finds interview times, emails candidates, and books the meeting. Here is the full flow and what it decides versus what you approve."
category: "scheduling"
keywords:
  - "AI interview scheduler"
  - "automated interview scheduling"
  - "schedule candidate interview"
  - "interview scheduling automation"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
  parent: "/guides/scheduling"
---

The AI auto-scheduler turns "find a time for this candidate and these three interviewers" into a sent email and a booked calendar event. You start it from a candidate's detail page; it handles the search and the back-and-forth. You approve the emails, or you let it send them.

## Starting a scheduling request

<Steps>
  <li>Open the candidate's detail page and click **Schedule**.</li>
  <li>Pick the interview type. If you've set up [interview templates](/guides/scheduling/interview-templates), the required interviewers come pre-filled.</li>
  <li>Confirm or adjust the interviewers, the meeting length, and the meeting mode — a Google Calendar event with a Meet link, a [Zoom](/getting-started/integrations/zoom) meeting, or manual times.</li>
  <li>Optionally add [natural-language constraints](/guides/scheduling/nl-constraints) like "avoid Friday afternoons."</li>
  <li>Choose whether emails send automatically or wait for your approval, then start the request.</li>
</Steps>

## What the scheduler does next

Once the request is running, the scheduler:

<Steps>
  <li>Reads [Google Calendar](/getting-started/integrations/google-calendar) free/busy for every interviewer selected, inside each one's [availability window](/guides/scheduling/availability-windows).</li>
  <li>Finds three to five viable slots in roughly the next five business days — the first real overlaps where everyone required is free.</li>
  <li>Drafts an email to the candidate offering those slots.</li>
  <li>Sends it automatically, or drops the draft into the [AI events tab](/guides/scheduling/ai-events-tab) for you to approve.</li>
  <li>When the candidate picks a slot, creates the calendar event and emails the interviewers the invite.</li>
</Steps>

A routine loop that used to take a day or two of email tag now takes about ten minutes of inbox latency.

## What it decides, what you approve

The scheduler **decides** which slots to offer — it does the calendar math you'd otherwise do by hand. It **decides** when there's a real overlap and books it.

You **approve** the candidate-facing email before it sends, unless you've opted into automatic sending. You **approve** any reply the [AI email assistant](/guides/scheduling/email-assistant) drafts when a candidate writes back. The booking itself isn't a question it asks you — once the candidate picks a slot everyone is free for, it books.

<Callout type="info" title="It waits for everyone required before booking">
The scheduler holds out for the first slot where every required interviewer is free. Optional interviewers can be skipped if they don't fit. It won't book a partial loop and hope — if no overlap exists, it tells you instead.
</Callout>

## When it gets it wrong

It will, sometimes. The usual causes:

- **It offered a time an interviewer can't make.** Their calendar had a tentative event marked "free," or an event they never added. The scheduler trusts the calendar. Fix the calendar, or reschedule manually from the candidate's detail page.
- **No slots came back at all.** Availability windows are too tight, calendars are too full, or [natural-language constraints](/guides/scheduling/nl-constraints) ruled out everything. The scheduler stops and tells you rather than guessing — loosen a window or relax a constraint.
- **The candidate went quiet.** They got the email and didn't pick a slot. The [AI events tab](/guides/scheduling/ai-events-tab) flags this after a few days so you can nudge them.

## Limits

- The search horizon is about five business days. It won't offer times a month out.
- It schedules one interview at a time, not a same-day multi-stage loop in a single pass.
- Without a connected calendar it can't see free/busy — scheduling still works, but you pick the times by hand. See [connecting Google Calendar](/getting-started/integrations/google-calendar).
